/**
 * Watch Tracker Service
 * Handles watch event and progress tracking API calls
 * Matches Flutter TV implementation
 */
import httpService from './http.service';
import { getCMSApiRoute } from '../utils/api.utils';

const WATCH_TRACKER_ENDPOINT = 'action/watch-tracker';

/**
 * Send initial watch event when content starts playing
 * @param {string} slug - Content slug/key
 */
export async function sendWatchEvent(slug) {
    if (!slug) {
        console.warn('[WatchTracker] sendWatchEvent: Invalid slug');
        return;
    }

    try {
        // The watch event is essentially a POST with time=0
        await httpService.post(getCMSApiRoute(WATCH_TRACKER_ENDPOINT), {
            contentKey: slug,
            isEpisode: 0,
            time: 0,
        });
        console.log('[WatchTracker] Watch event sent for:', slug);
    } catch (error) {
        console.warn('[WatchTracker] Watch event error:', error);
    }
}

/**
 * Send watch progress tracker
 * @param {Object} params - Watch tracker parameters
 * @param {string} params.slug - Content slug/key
 * @param {number} params.minutes - Current position in minutes
 * @param {boolean} params.isEpisode - Is this a series/program episode
 * @param {number} params.season - Season number (for episodes)
 * @param {number} params.episode - Episode number (for episodes)
 */
export async function sendWatchTracker({ slug, minutes, isEpisode = false, season, episode }) {
    if (!slug) {
        console.warn('[WatchTracker] sendWatchTracker: Invalid slug');
        return;
    }

    try {
        const params = {
            contentKey: slug,
            isEpisode: isEpisode ? 1 : 0,
            time: minutes,
        };

        if (isEpisode && season != null) {
            params.season = season;
        }
        if (isEpisode && episode != null) {
            params.episode = episode;
        }

        await httpService.post(getCMSApiRoute(WATCH_TRACKER_ENDPOINT), params);
        console.log('[WatchTracker] Progress reported:', minutes, 'minutes for:', slug);
    } catch (error) {
        console.warn('[WatchTracker] Watch tracker error:', error);
    }
}

/**
 * Load continue watch position from API
 * @param {Object} params - Query parameters
 * @param {string} params.slug - Content slug/key
 * @param {boolean} params.isEpisode - Is this a series/program episode
 * @param {number} params.season - Season number (for episodes)
 * @param {number} params.episode - Episode number (for episodes)
 * @returns {number} Elapsed time in minutes, or 0 if not found
 */
export async function loadContinueWatch({ slug, isEpisode = false, season, episode }) {
    if (!slug) {
        console.warn('[WatchTracker] loadContinueWatch: Invalid slug');
        return 0;
    }

    try {
        const params = {
            contentKey: encodeURIComponent(slug),
        };

        if (isEpisode) {
            params.isEpisode = 1;
            if (season != null) params.season = season;
            if (episode != null) params.episode = episode;
        }

        const response = await httpService.get(getCMSApiRoute(WATCH_TRACKER_ENDPOINT), { params });

        if (response.status !== 200) return 0;

        const results = response.data?.data?.results;
        if (Array.isArray(results) && results.length > 0) {
            const result = results[0];
            const elapsedMinutes = result?.options?.elapsedTime || 0;
            console.log('[WatchTracker] Loaded position:', elapsedMinutes, 'minutes for:', slug);
            return typeof elapsedMinutes === 'number' ? elapsedMinutes : parseInt(elapsedMinutes, 10) || 0;
        }

        return 0;
    } catch (error) {
        console.warn('[WatchTracker] Load continue watch error:', error);
        return 0;
    }
}

export default {
    sendWatchEvent,
    sendWatchTracker,
    loadContinueWatch,
};
